import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { supabase } from "@/lib/supabaseClient";

const TOPIC_LIST = [
  "Junk Journaling",
  "Snail Mail",
  "Ephemera Swaps",
  "Washi Tape",
  "Stamping & Ink",
  "Mixed Media",
  "Paper Crafting",
  "Bookbinding",
  "Penpal Exchange",
  "Art Journaling",
];

const GROUP_TYPES = ["Public", "Private", "Buy/Sell/Trade", "Swap Group", "Educational", "Inspiration"];
const MEMBER_RANGES = ["Under 500", "500–2,000", "2,000–10,000", "10,000–50,000", "50,000+"];

type FormState = {
  groupName: string;
  adminName: string;
  email: string;
  facebookUrl: string;
  description: string;
  memberCount: string;
  groupType: string;
  topics: string[];
  location: string;
};

const initialForm: FormState = {
  groupName: "",
  adminName: "",
  email: "",
  facebookUrl: "",
  description: "",
  memberCount: "",
  groupType: "",
  topics: [],
  location: "",
};

export default function GroupSubmitPage() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = "List Your Facebook Group — Junk Journal Community Directory | Junk & Post";
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const toggleTopic = (topic: string) => {
    setForm((prev) => ({
      ...prev,
      topics: prev.topics.includes(topic)
        ? prev.topics.filter((t) => t !== topic)
        : [...prev.topics, topic],
    }));
  };

  const handleImageChange = (file: File) => {
    if (!file.type.startsWith("image/")) {
      setError("Please upload a valid image file (JPG, PNG, or WebP).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be under 5MB.");
      return;
    }
    setError("");
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleImageChange(file);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.description.length > 500) {
      setError("Description must be 500 characters or less.");
      return;
    }
    if (!form.groupType) {
      setError("Please select a group type.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      let imageUrl: string | null = null;

      if (imageFile) {
        const ext = imageFile.name.split(".").pop();
        const fileName = `groups/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from("creator-images")
          .upload(fileName, imageFile, { contentType: imageFile.type, upsert: false });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage
          .from("creator-images")
          .getPublicUrl(fileName);
        imageUrl = urlData.publicUrl;
      }

      const { error: sbError } = await supabase.from("facebook_groups").insert({
        group_name: form.groupName,
        admin_name: form.adminName,
        email: form.email,
        facebook_url: form.facebookUrl,
        description: form.description || null,
        member_count: form.memberCount || null,
        group_type: form.groupType,
        topics: form.topics.join(", ") || null,
        location: form.location || null,
        image_url: imageUrl,
        approved: false,
        featured: false,
      });
      if (sbError) throw sbError;
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f4]">
      <Navbar />

      {/* Header */}
      <div className="pt-24 pb-12 bg-[#3a2410]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[#f0c87a] text-xs font-semibold uppercase tracking-widest mb-4">
            <i className="ri-facebook-circle-line"></i>
            List Your Group
          </div>
          <h1
            className="text-4xl font-bold text-white mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Submit Your Facebook Group
          </h1>
          <p className="text-[#c9a87c] max-w-xl mx-auto">
            Get your community discovered by junk journalers and paper crafters across the world.
            All submissions are reviewed before going live.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {submitted ? (
          <div className="bg-white rounded-2xl border border-[#c9a87c]/40 p-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5 rounded-full bg-[#f5ede0] text-[#8b5e3c]">
              <i className="ri-checkbox-circle-line text-4xl"></i>
            </div>
            <h2
              className="text-3xl font-bold text-[#2c1e0f] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Group Submitted!
            </h2>
            <p className="text-[#7a6555] mb-6 max-w-md mx-auto">
              Thanks for listing your group! We&apos;ll review it and publish it within 3–5 business days.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => { setSubmitted(false); setForm(initialForm); setImageFile(null); setImagePreview(null); }}
                className="px-6 py-3 rounded-full bg-[#8b5e3c] text-white font-semibold text-sm hover:bg-[#7a5234] transition-colors cursor-pointer whitespace-nowrap"
              >
                Submit Another Group
              </button>
              <Link
                to="/groups"
                className="px-6 py-3 rounded-full border border-[#c9a87c] text-[#8b5e3c] font-semibold text-sm hover:bg-[#f5ede0] transition-colors whitespace-nowrap text-center"
              >
                Browse All Groups
              </Link>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl border border-[#ede5d8] p-8 flex flex-col gap-6"
          >
            {/* Section: Group Info */}
            <div>
              <h2
                className="font-bold text-[#2c1e0f] text-lg mb-4 pb-2 border-b border-[#ede5d8]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Group Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                    Group Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="groupName"
                    value={form.groupName}
                    onChange={handleChange}
                    placeholder="e.g. Junk Journal Addicts"
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                    Your Name (Admin) <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="adminName"
                    value={form.adminName}
                    onChange={handleChange}
                    placeholder="Jane Doe"
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                    Contact Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="admin@yourgroup.com"
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                    Facebook Group URL <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="url"
                    name="facebookUrl"
                    value={form.facebookUrl}
                    onChange={handleChange}
                    placeholder="https://www.facebook.com/groups/yourgroup"
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                    Location (optional)
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                    placeholder="USA, Global, UK..."
                    className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c]"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                    Approximate Member Count
                  </label>
                  <select
                    name="memberCount"
                    value={form.memberCount}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] bg-white focus:outline-none focus:border-[#c9a87c] cursor-pointer"
                  >
                    <option value="">Select range</option>
                    {MEMBER_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Section: Group Type */}
            <div>
              <h2
                className="font-bold text-[#2c1e0f] text-lg mb-4 pb-2 border-b border-[#ede5d8]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Group Type <span className="text-red-400 text-sm font-normal">*</span>
              </h2>
              <div className="flex flex-wrap gap-3">
                {GROUP_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm({ ...form, groupType: t })}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      form.groupType === t
                        ? "bg-[#8b5e3c] text-white"
                        : "bg-[#f5ede0] text-[#8b5e3c] hover:bg-[#ede0cc]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Section: Topics */}
            <div>
              <h2
                className="font-bold text-[#2c1e0f] text-lg mb-4 pb-2 border-b border-[#ede5d8]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Topics &amp; Themes
              </h2>
              <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-2 block">
                What does your group focus on? (select all that apply)
              </label>
              <div className="flex flex-wrap gap-2">
                {TOPIC_LIST.map((topic) => (
                  <button
                    key={topic}
                    type="button"
                    onClick={() => toggleTopic(topic)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      form.topics.includes(topic)
                        ? "bg-[#8b5e3c] text-white"
                        : "bg-[#f5ede0] text-[#8b5e3c] hover:bg-[#ede0cc]"
                    }`}
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>

            {/* Section: Description + Banner */}
            <div>
              <h2
                className="font-bold text-[#2c1e0f] text-lg mb-4 pb-2 border-b border-[#ede5d8]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                About Your Group
              </h2>

              {/* Banner Image */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                  Group Banner / Cover Photo
                  <span className="ml-2 text-[#9a8a78] normal-case font-normal tracking-normal">
                    (optional — JPG, PNG or WebP, max 5MB)
                  </span>
                </label>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleFileDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`relative h-36 rounded-xl border-2 border-dashed transition-colors cursor-pointer overflow-hidden ${
                    dragOver
                      ? "border-[#8b5e3c] bg-[#f5ede0]"
                      : "border-[#ede5d8] hover:border-[#c9a87c] hover:bg-[#faf8f4]"
                  }`}
                >
                  {imagePreview ? (
                    <>
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImageFile(null);
                          setImagePreview(null);
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                        className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-black/50 text-white text-sm hover:bg-black/70 cursor-pointer"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full gap-2">
                      <div className="w-8 h-8 flex items-center justify-center text-[#c9a87c]">
                        <i className="ri-upload-cloud-2-line text-2xl"></i>
                      </div>
                      <p className="text-sm text-[#7a6555]">
                        <span className="font-semibold text-[#8b5e3c]">Click to upload</span> or drag &amp; drop
                      </p>
                      <p className="text-xs text-[#9a8a78]">Recommended: 1200×400px banner image</p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageChange(file);
                  }}
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-[#3a2e1e] uppercase tracking-wide mb-1.5 block">
                  Group Description <span className="text-red-400">*</span>
                </label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) handleChange(e);
                  }}
                  placeholder="Tell the community what your group is about, what kind of posts members share, any rules or events..."
                  rows={4}
                  required
                  className="w-full px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c] resize-none"
                />
                <p className="text-xs text-[#9a8a78] mt-1 text-right">{form.description.length}/500</p>
              </div>
            </div>

            {error && (
              <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-3.5 rounded-full bg-[#8b5e3c] text-white font-semibold text-base hover:bg-[#7a5234] transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
              >
                {submitting ? "Submitting..." : "Submit My Group"}
              </button>
              <Link
                to="/groups"
                className="px-6 py-3.5 rounded-full border border-[#c9a87c] text-[#8b5e3c] font-semibold text-base hover:bg-[#f5ede0] transition-colors whitespace-nowrap text-center"
              >
                Cancel
              </Link>
            </div>
            <p className="text-xs text-[#9a8a78] text-center">
              By submitting, you confirm you are an admin or authorized representative of this group.
              We review all submissions within 3–5 business days.
            </p>
          </form>
        )}
      </div>

      <Footer />
    </div>
  );
}
